import React from 'react'
import PropTypes from 'prop-types'

// Components
import {Link as RouteLink} from 'react-router-dom'
import {List, ListItem, Text, Link} from '@salesforce/retail-react-app/app/components/shared/ui'

// Hooks
import {useBreakpointValue} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useIntl} from 'react-intl'
import useMultiSite from '../../hooks/use-multi-site'

// Utils
import {categoryUrlBuilder} from '../../utils/url'

// Custom Hooks
import {useGTM} from '@app/hooks/use-gtm'

/**
 * Navigation menu level 3 categories component
 *
 * @param {Object} props
 * @param {Object} props.item
 * @param {string} props.itemsKey
 * @param {boolean} props.isOpen
 * @param {string|Function} props.itemsFilter
 * @returns {React.Component}
 */
export const DrawerMenuLvl3Categories = ({
    item,
    itemsKey,
    isOpen,
    itemsFilter,
    ariaDescribedBy
}) => {
    const intl = useIntl()
    const itemsLvl3 = item[itemsKey] || []
    const isMobile = useBreakpointValue({
        base: true,
        md: false
    })
    const {buildUrl} = useMultiSite()
    const {triggerClickMenuEvent} = useGTM()

    const filter = (item) =>
        typeof itemsFilter === 'function' ? itemsFilter(item) : !!item[itemsFilter]

    /**
     * Triggers a click event for a menu item and integrates with Google Tag Manager (GTM).
     *
     * This function is responsible for handling the click event on a menu item.
     * It utilizes the `triggerClickMenuEvent` function to send the event data
     * to Google Tag Manager for tracking purposes.
     *
     * @param {Object} itemClicked - The menu item that was clicked. This object
     *                               contains details about the menu item, which
     *                               are used for event tracking.
     */
    const triggerClickMenu = (itemClicked) => {
        // GTM Integration
        triggerClickMenuEvent(itemClicked)
    }

    return (
        <List aria-describedby={ariaDescribedBy}>
            {itemsLvl3.filter(filter).map((itemLvl3, i) => {
                return (
                    <ListItem key={itemLvl3.id}>
                        <Link
                            as={RouteLink}
                            to={buildUrl(categoryUrlBuilder(itemLvl3, itemLvl3?.c_categoryType))}
                            textDecoration={'none'}
                            display={'block'}
                            tabIndex={isOpen ? 0 : -1}
                            onClick={() => triggerClickMenu(itemLvl3)}
                        >
                            <Text
                                variant={isMobile ? 'bodyBase2' : 'bodySmall'}
                                color="darkGray"
                                p={[i === 0 ? '0 0 7px' : '7px 0 7px', null, '3px 0 3px']}
                            >
                                {itemLvl3?.name}
                            </Text>
                        </Link>
                    </ListItem>
                )
            })}
            <ListItem>
                <Link
                    as={RouteLink}
                    to={buildUrl(categoryUrlBuilder(item, item?.c_categoryType))}
                    display={'block'}
                    tabIndex={isOpen ? 0 : -1}
                    onClick={() => triggerClickMenu(item)}
                >
                    <Text
                        variant={isMobile ? 'bodyBase2' : 'bodySmall'}
                        fontWeight={'500'}
                        color="darkGray"
                        py={['7px', null, '3px']}
                    >
                        {intl.formatMessage(
                            {
                                id: 'drawer_menu.link.shop_all',
                                defaultMessage: 'View all {name}'
                            },
                            {
                                name: ''
                            }
                        )}
                    </Text>
                </Link>
            </ListItem>
        </List>
    )
}

DrawerMenuLvl3Categories.propTypes = {
    item: PropTypes.object.isRequired,
    itemsKey: PropTypes.string,
    itemsCountKey: PropTypes.string,
    itemsFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    isOpen: PropTypes.bool,
    ariaDescribedBy: PropTypes.string
}
